import type { StaffMemoryFact } from "@lafayette/schemas";
import { type ReactNode, useId } from "react";
import { Link } from "react-router";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { Icon } from "../../ui/icon";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { confidencePercent, confirmedPhrase, expiryPhrase } from "../format";
import {
  factValueLabel,
  formLabels,
  kindLabel,
  memoryStatusLabels,
  provenanceLabels,
  reviewReasonLabels,
  sensitivityLabels,
} from "../labels";

// The small pieces every memory screen shares. A badge always carries a glyph and a word, never a colour alone.
//
// NO MEMORY VALUE IN ANY ATTRIBUTE (docs/engineering/admin-memory-review.md). A value goes into the TEXT of the page only: the error
// reporter copies `aria-label`, `title`, `alt`, `data-*` and the id and classes of a clicked element into a breadcrumb. A control is named
// with `aria-labelledby`, which points at text that is already on the page (the verb, the kind, the position in its list); the value is
// its description (`aria-describedby`).

const statusTones: Readonly<Record<StaffMemoryFact["status"], StatusTone>> = {
  CANDIDATE: "info",
  ACTIVE: "success",
  DISPUTED: "warning",
  SUPERSEDED: "neutral",
  EXPIRED: "neutral",
  DELETED: "neutral",
};

const statusGlyphs: Readonly<Record<StaffMemoryFact["status"], ReactNode>> = {
  CANDIDATE: <Icon name="help-circle" />,
  ACTIVE: <Icon name="checkmark-circle-02" />,
  DISPUTED: <Icon name="alert-02" />,
  SUPERSEDED: <Icon name="refresh" />,
  EXPIRED: <Icon name="hourglass" />,
  DELETED: <Icon name="cancel-circle" />,
};

/** The state of a fact. A fact that is not in use (a candidate or a closed one) has a dashed border as well as its word. */
export function FactStatusBadge({ status }: Readonly<{ status: StaffMemoryFact["status"] }>) {
  return (
    <StatusBadge
      tone={statusTones[status]}
      glyph={statusGlyphs[status]}
      className={status === "ACTIVE" ? "" : "border-dashed"}
    >
      {memoryStatusLabels[status]}
    </StatusBadge>
  );
}

/**
 * Where a fact came from. A fact that no person confirmed shows a warning glyph and its own words: an inference never reads as a
 * statement of the customer. The inferred fact is dashed and amber, so it can never pass for a certain declaration.
 */
export function ProvenanceBadge({
  provenance,
}: Readonly<{ provenance: StaffMemoryFact["provenance"] }>) {
  const label = provenanceLabels[provenance];
  const tone: StatusTone = label.certain
    ? "success"
    : provenance === "MODEL_INFERRED"
      ? "warning"
      : "info";
  const glyph = label.certain ? (
    <Icon name="user-check-01" />
  ) : provenance === "MODEL_INFERRED" ? undefined : (
    <Icon name="help-circle" />
  );
  return (
    <StatusBadge
      tone={tone}
      glyph={glyph}
      className={cn("whitespace-normal", label.certain ? undefined : "border-dashed")}
    >
      {label.badge}
    </StatusBadge>
  );
}

/**
 * The value of a fact, large, with its kind heading kept by the caller. `id` lets a button or a link say, with `aria-describedby`, which
 * value it acts on. The value goes into the TEXT of the page only.
 */
export function FactValue({
  fact,
  id,
}: Readonly<{ fact: StaffMemoryFact; id?: string | undefined }>) {
  const erased = fact.value === null;
  return (
    <p
      id={id}
      className={cn(
        "m-0 flex flex-wrap items-center gap-2 text-lg leading-[26px] font-semibold [overflow-wrap:anywhere]",
        erased ? "font-semibold text-muted-foreground italic" : undefined,
      )}
    >
      {factValueLabel(fact)}
      {fact.form === "TEXT" && !erased ? <Badge tone="outline">{formLabels.TEXT}</Badge> : null}
    </p>
  );
}

/** The grid of a list of terms and values (`dl`) that the memory screens share. */
export function MetaList({
  children,
  wide = false,
}: Readonly<{ children: ReactNode; wide?: boolean }>) {
  return (
    <dl
      className={cn(
        "m-0 grid gap-x-6 gap-y-3",
        wide
          ? "grid-cols-[repeat(auto-fit,minmax(min(100%,12.5rem),1fr))]"
          : "grid-cols-[repeat(auto-fit,minmax(min(100%,11rem),1fr))]",
      )}
    >
      {children}
    </dl>
  );
}

export function MetaItem({ term, children }: Readonly<{ term: string; children: ReactNode }>) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold text-muted-foreground">{term}</dt>
      <dd className="m-0 text-sm [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** The facts under a value: origin, freshness, expiry, version, and the hints that never approve it. */
export function FactMeta({
  fact,
  now,
  compact = false,
}: Readonly<{
  fact: StaffMemoryFact;
  now: Date;
  /** Leave out origin, freshness and expiry: the screen already shows them (the side-by-side review). */
  compact?: boolean;
}>) {
  const label = provenanceLabels[fact.provenance];
  return (
    <MetaList>
      {compact ? null : <MetaItem term="Origin">{label.detail}</MetaItem>}
      {compact || fact.status === "DELETED" ? null : (
        <MetaItem term="Freshness">{confirmedPhrase(fact.lastConfirmedAt, now, fact)}</MetaItem>
      )}
      {!compact && (fact.status === "ACTIVE" || fact.status === "DISPUTED") ? (
        <MetaItem term="Expiry">{expiryPhrase(fact.expiresAt, now)}</MetaItem>
      ) : null}
      {fact.reviewReason === null ? null : (
        <MetaItem term="Why it needs a review">{reviewReasonLabels[fact.reviewReason]}</MetaItem>
      )}
      {fact.confidencePermille === null ? null : (
        <MetaItem term="Assistant confidence">
          {confidencePercent(fact.confidencePermille)}{" "}
          <span className="text-muted-foreground">A hint only. It never approves a memory.</span>
        </MetaItem>
      )}
      <MetaItem term="Version">{fact.version}</MetaItem>
    </MetaList>
  );
}

/** The flags of a fact that a reader must not miss. */
function FactFlags({ fact }: Readonly<{ fact: StaffMemoryFact }>) {
  return (
    <>
      {fact.sensitivity === "STANDARD" ? null : (
        <Badge tone="outline">{sensitivityLabels[fact.sensitivity]}</Badge>
      )}
      {fact.contested ? <StatusBadge tone="warning">Challenged</StatusBadge> : null}
    </>
  );
}

/** The state of a fact in one row: its status, where it came from and its flags. */
export function FactBadges({ fact }: Readonly<{ fact: StaffMemoryFact }>) {
  return (
    <div className="flex flex-wrap gap-2">
      <FactStatusBadge status={fact.status} />
      <ProvenanceBadge provenance={fact.provenance} />
      <FactFlags fact={fact} />
    </div>
  );
}

/** Where a fact sits in the list that holds it: "active memory 2 of 5". It makes the name of its controls unique. */
export type FactPosition = Readonly<{ noun: string; index: number; total: number }>;

/**
 * The context of the controls of one fact, as text on the page: its kind and its position ("Dietary preference, active memory 2 of 5").
 * The controls name themselves with `aria-labelledby` = their own id + this id, so two cards never share a name (WALK-3) and no value
 * is in an attribute. With `visible` the kind shows (lists that do not group by kind); the position is always for a screen reader only.
 */
export function FactContext({
  id,
  kind,
  position,
  visible = false,
}: Readonly<{
  id: string;
  kind: StaffMemoryFact["kind"];
  position: FactPosition | undefined;
  visible?: boolean;
}>) {
  const where =
    position === undefined ? null : `, ${position.noun} ${position.index} of ${position.total}`;
  return (
    <p
      id={id}
      className={
        visible
          ? "m-0 text-xs font-bold tracking-[0.06em] text-muted-foreground uppercase"
          : "sr-only"
      }
    >
      {kindLabel(kind)}
      {where === null ? null : <span className="sr-only">{where}</span>}
    </p>
  );
}

export function detailPath(customerId: string, factId: string): string {
  return `/customers/${encodeURIComponent(customerId)}/memory/facts/${encodeURIComponent(factId)}`;
}

/** A name for a control: its own words, then the text that says which fact it acts on (see `FactContext`). */
export function nameWith(ownId: string, contextId: string | undefined): string | undefined {
  return contextId === undefined ? undefined : `${ownId} ${contextId}`;
}

export function DetailLink({
  customerId,
  fact,
  describedBy,
  contextId,
}: Readonly<{
  customerId: string;
  fact: StaffMemoryFact;
  describedBy?: string | undefined;
  /** The id of the `FactContext` text that names this link after its own word. */
  contextId?: string | undefined;
}>) {
  const ownId = useId();
  return (
    <Button asChild variant="outline" size="sm">
      <Link
        id={ownId}
        to={detailPath(customerId, fact.id)}
        aria-describedby={describedBy}
        aria-labelledby={nameWith(ownId, contextId)}
      >
        Details
      </Link>
    </Button>
  );
}

/** A warning that is not an error: a truncated list, a paused setting. A glyph and words; `role` says how a screen reader gets it. */
export function WarningNote({
  role = "status",
  children,
}: Readonly<{ role?: "status" | "note"; children: ReactNode }>) {
  return (
    <p
      role={role}
      className="m-0 flex items-start gap-2 rounded-sm border border-warning-border bg-warning-soft px-3.5 py-2.5 text-sm leading-6 text-warning"
    >
      <Icon name="alert-02" className="mt-1 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** The jump links under a page title: they move focus within the page, so they are plain links. */
export function JumpLinks({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <nav aria-label={label} className="flex flex-wrap gap-x-6 gap-y-1">
      {children}
    </nav>
  );
}

export function JumpLink({ href, children }: Readonly<{ href: string; children: ReactNode }>) {
  return (
    <a
      href={href}
      className="inline-flex min-h-11 items-center text-sm font-medium text-link underline-offset-4 hover:underline compact:min-h-8"
    >
      {children}
    </a>
  );
}
